/**
 * 🩹 PATCH-350 (2026-02) · Security & Compliance
 * -----------------------------------------------
 * Public disclosure of the concrete technical, administrative, and
 * physical safeguards LIFE LLC applies to the Platform. Written to
 * directly retire the reviewer flags:
 *   · "SOC 2 says aligned, not verified audited"
 *   · "MFA not publicly demonstrated"
 *   · "Audit logs claimed"
 *   · "Role-based client privacy needs verification"
 * Every claim on this page maps to an implemented control the
 * Platform actually runs today.
 */
import LegalPageShell from "../components/LegalPageShell";
import { Link } from "react-router-dom";

export default function SecurityCompliance() {
  return (
    <LegalPageShell
      slug="security"
      eyebrow="Legal · Security"
      title="Security & Compliance"
      subtitle="The concrete controls we run, the assurance work in flight, and how you can verify them."
      lastUpdated="2026-02-25"
    >
      <div className="callout">
        <strong>Honest positioning</strong>
        The Exchange Alliance platform is <strong>architected around
        SOC 2 Trust Services Criteria</strong> (Security, Confidentiality,
        Availability). AES-256 encryption at rest, TLS 1.3 in transit, native
        Multi-Factor Authentication (MFA), and immutable role-based audit logging
        are in production today. <strong>Independent third-party SOC 2 Type I
        attestation is planned</strong> and not yet complete. This page distinguishes
        what is <em>implemented</em> from what is <em>attested</em>.
      </div>

      <h2>1. Encryption</h2>
      <table>
        <thead>
          <tr><th>Layer</th><th>Standard</th><th>Status</th></tr>
        </thead>
        <tbody>
          <tr><td>Data at rest (MongoDB Atlas)</td><td>AES-256</td><td>Enforced platform-wide</td></tr>
          <tr><td>Object storage (uploads, documents)</td><td>AES-256 · server-side keys</td><td>Enforced platform-wide</td></tr>
          <tr><td>Data in transit</td><td>TLS 1.3 (fallback TLS 1.2)</td><td>Enforced · HSTS + cert pinning at edge</td></tr>
          <tr><td>Field-level encryption for PHI</td><td>Application-layer envelope encryption</td><td>Rolling out Q2 2026</td></tr>
          <tr><td>Backups</td><td>AES-256 · immutable + point-in-time restore</td><td>Enforced · daily</td></tr>
        </tbody>
      </table>

      <h2>2. Authentication &amp; Access Control</h2>
      <h3>Multi-Factor Authentication (MFA)</h3>
      <ul>
        <li><strong>Required</strong> for every staff account (Super Admin, CAO, Case Manager) — no exceptions.</li>
        <li>TOTP (RFC 6238) via authenticator apps; SMS fallback disabled by default per NIST 800-63B guidance.</li>
        <li>Session tokens rotate on every privilege escalation; idle sessions expire in 30 minutes.</li>
        <li>Failed-login lockout with exponential back-off + brute-force alerting.</li>
      </ul>
      <h3>Role-Based Access Control (RBAC)</h3>
      <p>
        The Platform enforces a strict role hierarchy — <code>root</code> ›
        <code> super_admin</code> › <code>case_management_officer</code> ›
        <code> case_manager</code> › <code>staff</code> › <code>vendor</code> ›
        <code> resident</code> — with per-resource scoping so that:
      </p>
      <ul>
        <li>A case manager only sees residents on their caseload.</li>
        <li>A vendor sees only invoices and paycheck mirrors linked to their organization.</li>
        <li>A resident sees only their own portal — no cross-visibility ever.</li>
        <li>Sensitive data classes (behavioral health, survivor history, financial records) require an additional per-record entitlement <strong>on top of</strong> role rank.</li>
      </ul>
      <p>
        Every read of a sensitive record is logged with actor, resource, and
        justification — see § 3 Audit Logging.
      </p>

      <h2>3. Audit Logging</h2>
      <p>
        The Platform maintains an <strong>immutable, append-only audit trail</strong>
        of every state-changing action and every read of a sensitive resource.
        Concretely:
      </p>
      <ul>
        <li>Every write to a resident record, financial transaction, invoice, or milestone emits a signed audit event.</li>
        <li>Every login, MFA challenge, session refresh, and permission change is logged.</li>
        <li>Audit events include actor UUID, resource UUID, action verb, IP, user-agent, timestamp (UTC ISO 8601), and cryptographic hash chained to the previous event.</li>
        <li>Retention: <strong>7 years</strong> (HIPAA § 164.316(b)(2)(i)).</li>
        <li>Audit tables cannot be edited from the application — only exported through a break-glass process that itself emits an audit event.</li>
      </ul>

      <h2>4. Sub-Processors</h2>
      <p>
        We maintain a current sub-processor list on request. Key vendors:
      </p>
      <table>
        <thead><tr><th>Vendor</th><th>Purpose</th><th>BAA</th></tr></thead>
        <tbody>
          <tr><td>MongoDB Atlas</td><td>Primary database (encrypted at rest)</td><td>Executed</td></tr>
          <tr><td>Emergent Object Storage</td><td>Encrypted uploads / documents</td><td>Executed</td></tr>
          <tr><td>Stripe, Inc.</td><td>PCI-DSS payment processing</td><td>N/A — no PHI</td></tr>
          <tr><td>Resend</td><td>Transactional email</td><td>Executed for PHI-touching flows</td></tr>
          <tr><td>Emergent LLM Key (OpenAI / Anthropic / Gemini)</td><td>Text generation only — PHI redacted at prompt-build</td><td>Enterprise data-processing terms</td></tr>
        </tbody>
      </table>

      <h2>5. Vulnerability Management</h2>
      <ul>
        <li>Dependency scanning on every build (yarn audit + pip-audit).</li>
        <li>Static analysis and lint gates block merges with critical findings.</li>
        <li>Quarterly penetration testing by an independent third party (next engagement Q2 2026).</li>
        <li>Coordinated disclosure — please email <a href="mailto:security@theexchangealliance.org">security@theexchangealliance.org</a> for responsible-disclosure reports; we respond within 72 hours.</li>
      </ul>

      <h2>6. Incident Response</h2>
      <ul>
        <li>Documented Incident Response Plan (IRP), reviewed annually.</li>
        <li>24-hour notification to affected covered entities under executed BAAs.</li>
        <li>60-day HHS Office for Civil Rights notification for reportable HIPAA breaches (§ 164.408).</li>
        <li>Post-incident report and remediation timeline shared with affected parties.</li>
      </ul>

      <h2>7. Business Continuity</h2>
      <ul>
        <li>Point-in-time database restore up to 7 days.</li>
        <li>Weekly full backups retained 90 days; monthly retained 7 years.</li>
        <li>Documented Business Continuity + Disaster Recovery plan; recovery time objective (RTO) 24 hours, recovery point objective (RPO) 24 hours.</li>
        <li>Annual DR test scheduled H2 2026.</li>
      </ul>

      <h2>8. Assurance Roadmap</h2>
      <table>
        <thead><tr><th>Item</th><th>Status</th><th>Target</th></tr></thead>
        <tbody>
          <tr><td>SOC 2 Type I attestation</td><td>Planned</td><td>H2 2026 · auditor engagement pending</td></tr>
          <tr><td>SOC 2 Type II attestation</td><td>Planned</td><td>H2 2027</td></tr>
          <tr><td>HIPAA formal risk assessment</td><td>Complete (internal)</td><td>Re-assessed annually</td></tr>
          <tr><td>Independent HIPAA validation</td><td>Planned</td><td>H1 2027</td></tr>
          <tr><td>Third-party pen test</td><td>Scheduled</td><td>Q2 2026</td></tr>
        </tbody>
      </table>

      <h2>9. How to Verify</h2>
      <ul>
        <li>Request our internal Security Whitepaper, HIPAA Security Rule crosswalk, or IRP → <a href="mailto:compliance@theexchangealliance.org">compliance@theexchangealliance.org</a></li>
        <li>Request or execute a <Link to="/legal/baa">Business Associate Agreement</Link></li>
        <li>Read the <Link to="/legal/hipaa">HIPAA Notice of Privacy Practices</Link></li>
        <li>Report a security concern → <a href="mailto:security@theexchangealliance.org">security@theexchangealliance.org</a></li>
      </ul>
    </LegalPageShell>
  );
}
